import { z } from "zod";
import { isValidPhone, toE164 } from "@/lib/phone";
import type { CountryCode } from "libphonenumber-js/max";

/**
 * Step 1 — who we represent, and for whom.
 *
 * The client's name is the one thing every deal has from the very first
 * moment, which is why it now leads the wizard. Before this it was collected
 * nowhere at all: the launcher fell back to the literal string "Client", so
 * every deal opened through this flow was anonymous.
 */
// Every represented client is invited to the deal room the moment it's
// created, so their email is required — an invitation needs an address to
// send to.
const requiredEmailField = z.string().trim().min(1, "Email is required.").refine(
  (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value),
  "Enter a valid email address.",
);

// A co-buyer/co-seller beyond the primary client — same side, e.g. a spouse
// or co-owner.
export const additionalClientSchema = z.object({
  name: z.string().trim().min(2, "Name is required."),
  email: requiredEmailField,
  phone: z.string().trim().max(32, "Phone number is too long."),
  // Form-local: the ISO country `phone` is read against, same split as
  // PhoneField everywhere else. Never sent to the server — resolved to E.164
  // (or dropped) before submit.
  phone_country: z.string().trim().default("US"),
});

export const newDealTransactionSchema = z.object({
  deal_type: z.enum(["buy", "sell"]),
  client_name: z.string().trim().min(2, "Client name is required."),
  client_email: requiredEmailField,
  client_phone: z.string().trim().max(32, "Phone number is too long."),
  client_phone_country: z.string().trim().default("US"),
  co_agent_name: z.string().trim().max(120, "Name is too long.").default(""),
  co_agent_email: z.string().trim().default(""),
  co_agent_phone: z.string().trim().max(32, "Phone number is too long.").default(""),
  co_agent_phone_country: z.string().trim().default("US"),
  // Additional represented clients on the same side (co-buyers/co-sellers).
  additional_clients: z.array(additionalClientSchema).default([]),
  // "Already under contract" at intake. It is what re-arms the address
  // requirement on a buyer deal — you cannot be under contract on a house you
  // have not identified.
  under_contract: z.boolean().default(false),
}).superRefine((values, ctx) => {
  const seenEmails = new Map<string, (string | number)[]>();
  const seenPhones = new Map<string, (string | number)[]>();
  const trackUnique = (
    seen: Map<string, (string | number)[]>,
    value: string | null | undefined,
    path: (string | number)[],
    message: string,
  ) => {
    if (!value) return;
    if (seen.has(value)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path, message });
      return;
    }
    seen.set(value, path);
  };

  trackUnique(seenEmails, values.client_email.trim().toLowerCase(), ["client_email"], "Each client needs a unique email address.");
  if (values.client_phone && !isValidPhone(values.client_phone, values.client_phone_country as CountryCode)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["client_phone"], message: "Enter a valid phone number." });
  } else if (values.client_phone) {
    trackUnique(
      seenPhones,
      toE164(values.client_phone, values.client_phone_country as CountryCode),
      ["client_phone"],
      "Each client needs a unique phone number.",
    );
  }
  values.additional_clients.forEach((client, index) => {
    trackUnique(
      seenEmails,
      client.email.trim().toLowerCase(),
      ["additional_clients", index, "email"],
      "Each client needs a unique email address.",
    );
    if (client.phone && !isValidPhone(client.phone, client.phone_country as CountryCode)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["additional_clients", index, "phone"],
        message: "Enter a valid phone number.",
      });
    } else if (client.phone) {
      trackUnique(
        seenPhones,
        toE164(client.phone, client.phone_country as CountryCode),
        ["additional_clients", index, "phone"],
        "Each client needs a unique phone number.",
      );
    }
  });
  // The other side's agent is only asked for once the deal is under contract;
  // before that there is no other side yet, and the hidden fields are cleared.
  if (!values.under_contract) return;
  if (values.co_agent_email) {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.co_agent_email)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["co_agent_email"], message: "Enter a valid email address." });
    } else {
      trackUnique(
        seenEmails,
        values.co_agent_email.trim().toLowerCase(),
        ["co_agent_email"],
        "Each invited person needs a unique email address.",
      );
    }
  } else if (values.co_agent_name || values.co_agent_phone) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["co_agent_email"], message: "Email is required to invite the other side's agent." });
  }
  if (values.co_agent_phone && !isValidPhone(values.co_agent_phone, values.co_agent_phone_country as CountryCode)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["co_agent_phone"], message: "Enter a valid phone number." });
  } else if (values.co_agent_phone) {
    trackUnique(
      seenPhones,
      toE164(values.co_agent_phone, values.co_agent_phone_country as CountryCode),
      ["co_agent_phone"],
      "Each invited person needs a unique phone number.",
    );
  }
});

/**
 * Step 2 — the property, which a buyer-side deal may not have yet.
 *
 * A buyer in the house-hunting phase has no address, no city and no ZIP, and
 * forcing them through those fields meant agents typed placeholder junk that
 * then showed up on the board, in emails and in the client's own portal
 * header. `addressRequired` is passed in from the side chosen on step 1.
 */
export const newDealPropertySchema = (addressRequired: boolean) => z.object({
  address: z.string().trim(),
  country: z.string().trim(),
  city: z.string().trim(),
  state: z.string().trim().transform((value) => value.toUpperCase()),
  zip_code: z.string().trim(),
  list_price: z.string().trim().refine(
    (value) => value === "" || (/^\d+(?:\.\d{1,2})?$/.test(value) && Number(value) > 0),
    "Enter a price greater than zero.",
  ),
  // Only asked when the deal opens under contract; they seed Contract Details.
  close_date: z.string().trim().optional().default(""),
  financing: z.enum(["financed", "cash"]).optional().default("financed"),
}).superRefine((values, ctx) => {
  if (!addressRequired) {
    // Optional does not mean unvalidated: a partial address is still wrong.
    // Whatever they chose to fill in has to be internally consistent, so the
    // ZIP check below still applies.
    if (values.country === "US" && values.zip_code && !/^\d{5}(?:-\d{4})?$/.test(values.zip_code)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["zip_code"], message: "Enter a valid ZIP code." });
    }
    return;
  }
  const required: Array<[keyof typeof values, number, string]> = [
    ["address", 3, "Property address is required."],
    ["country", 2, "Country is required."],
    ["city", 2, "City is required."],
    ["state", 2, "State is required."],
    ["zip_code", 3, "Postal code is required."],
  ];
  required.forEach(([field, min, message]) => {
    if (String(values[field] || "").length < min) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [field], message });
    }
  });
  if (values.country === "US" && !/^\d{5}(?:-\d{4})?$/.test(values.zip_code)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["zip_code"], message: "Enter a valid ZIP code." });
  }
});

export const newDealReviewSchema = z.object({});

export type NewDealTransactionValues = z.infer<typeof newDealTransactionSchema>;
export type NewDealReviewValues = z.infer<typeof newDealReviewSchema>;
